import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
// Import locales individually for better compatibility with date-fns v2
import ptBR from 'date-fns/locale/pt-BR';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import de from 'date-fns/locale/de';
import fr from 'date-fns/locale/fr';
import it from 'date-fns/locale/it';
import ja from 'date-fns/locale/ja';
import ko from 'date-fns/locale/ko';
import zhCN from 'date-fns/locale/zh-CN';
import zhTW from 'date-fns/locale/zh-TW';
import ru from 'date-fns/locale/ru';
import ar from 'date-fns/locale/ar-SA';
import nl from 'date-fns/locale/nl';
import pl from 'date-fns/locale/pl';
import tr from 'date-fns/locale/tr';
import vi from 'date-fns/locale/vi';
import th from 'date-fns/locale/th';
import id from 'date-fns/locale/id';
import uk from 'date-fns/locale/uk';
import cs from 'date-fns/locale/cs';
import el from 'date-fns/locale/el';
import he from 'date-fns/locale/he';
import hi from 'date-fns/locale/hi';
import hu from 'date-fns/locale/hu';
import ro from 'date-fns/locale/ro';
import sk from 'date-fns/locale/sk';
import sv from 'date-fns/locale/sv';
import da from 'date-fns/locale/da';
import fi from 'date-fns/locale/fi';
import nb from 'date-fns/locale/nb';
import ca from 'date-fns/locale/ca';

/**
 * Maps i18n locale codes to date-fns locale objects
 */
const LOCALE_MAP = {
  pt_BR: ptBR,
  pt: ptBR,
  en: enUS,
  en_US: enUS,
  es: es,
  de: de,
  fr: fr,
  it: it,
  ja: ja,
  ko: ko,
  zh_CN: zhCN,
  zh_TW: zhTW,
  zh: zhCN,
  ru: ru,
  ar: ar,
  nl: nl,
  pl: pl,
  tr: tr,
  vi: vi,
  th: th,
  id: id,
  uk: uk,
  cs: cs,
  el: el,
  he: he,
  hi: hi,
  hu: hu,
  ro: ro,
  sk: sk,
  sv: sv,
  da: da,
  fi: fi,
  nb: nb,
  no: nb,
  ca: ca,
};

/**
 * Composable that provides the date-fns locale based on current i18n locale
 * @returns {Object} - { dateFnsLocale: ComputedRef<Locale> }
 */
/**
 * Gets date-fns locale from a locale code string
 * Useful for Options API components
 * @param {string} localeCode - The locale code (e.g., 'pt_BR', 'en')
 * @returns {Locale} - The date-fns locale object
 */
export const getDateFnsLocale = localeCode => {
  // Try exact match first
  if (LOCALE_MAP[localeCode]) {
    return LOCALE_MAP[localeCode];
  }

  // Try base language (e.g., 'pt_BR' -> 'pt')
  const baseLocale = localeCode.split('_')[0];
  if (LOCALE_MAP[baseLocale]) {
    return LOCALE_MAP[baseLocale];
  }

  // Fallback to English
  return enUS;
};

/**
 * Composable that provides the date-fns locale based on current i18n locale
 * @returns {Object} - { dateFnsLocale: ComputedRef<Locale> }
 */
export const useDateFnsLocale = () => {
  const { locale } = useI18n();

  const dateFnsLocale = computed(() => {
    return getDateFnsLocale(locale.value);
  });

  return { dateFnsLocale };
};

export default useDateFnsLocale;
